import jsQR from 'jsqr';

/**
 * Finding a QR code inside a photograph.
 *
 * This half knows nothing about KHQR — it turns a `File` into a string and
 * stops there. What the string means is `@telestore/payment-core`'s problem.
 *
 * The reason this is a ladder rather than one call: on 2026-08-26 a single
 * jsQR pass over a photo of VILLA COFFEE's standee found nothing. The code
 * occupied about a seventh of the frame, shot at an angle under a glossy
 * plastic holder, and only came out after cropping to it and scaling up. A
 * merchant standing in their shop will take exactly that photo, so the naive
 * implementation would have failed on the very first real user.
 */

export interface ScanRegion {
  /** Source rectangle, in pixels of the (already downscaled) bitmap. */
  x: number;
  y: number;
  width: number;
  height: number;
  /** How much to magnify it before decoding. */
  scale: number;
}

/** Longest edge we hand to jsQR. Beyond this it is slower, not more accurate. */
export const MAX_WORKING_EDGE = 1600;

/**
 * Where to look, cheapest first. Whole frame, then progressively tighter
 * centre crops (a person photographing a standee aims at it), then a coarse
 * overlapping grid for a code that ended up off to one side.
 */
export function scanRegions(width: number, height: number): ScanRegion[] {
  const regions: ScanRegion[] = [{ x: 0, y: 0, width, height, scale: 1 }];

  for (const fraction of [0.6, 0.4]) {
    const w = Math.round(width * fraction);
    const h = Math.round(height * fraction);
    regions.push({
      x: Math.round((width - w) / 2),
      y: Math.round((height - h) / 2),
      width: w,
      height: h,
      scale: 2,
    });
  }

  // 3×3 tiles at half size, so neighbours overlap and a code on a seam still
  // falls entirely inside at least one tile.
  const tileW = Math.round(width / 2);
  const tileH = Math.round(height / 2);
  for (let row = 0; row < 3; row += 1) {
    for (let col = 0; col < 3; col += 1) {
      regions.push({
        x: Math.round((col * (width - tileW)) / 2),
        y: Math.round((row * (height - tileH)) / 2),
        width: tileW,
        height: tileH,
        scale: 2,
      });
    }
  }

  return regions;
}

function decodeRegion(
  bitmap: ImageBitmap,
  region: ScanRegion,
  canvas: HTMLCanvasElement,
): string | null {
  const width = Math.round(region.width * region.scale);
  const height = Math.round(region.height * region.scale);
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) return null;

  context.drawImage(
    bitmap,
    region.x,
    region.y,
    region.width,
    region.height,
    0,
    0,
    width,
    height,
  );

  const { data } = context.getImageData(0, 0, width, height);
  // `attemptBoth` costs a second pass but catches codes printed light-on-dark,
  // and some shop signage is.
  return jsQR(data, width, height, { inversionAttempts: 'attemptBoth' })?.data ?? null;
}

/**
 * Read the first QR code found in an image file, or null.
 *
 * Nothing leaves the browser: the file is decoded, scanned and dropped. The
 * photograph of a merchant's bank QR is a means to five short strings, and
 * keeping it would mean storing an image of their account number for no
 * further use.
 */
export async function scanImageFile(file: File): Promise<string | null> {
  const source = await createImageBitmap(file, { imageOrientation: 'from-image' });

  // Downscale once, then every region below works off the smaller bitmap.
  const longestEdge = Math.max(source.width, source.height);
  const ratio = longestEdge > MAX_WORKING_EDGE ? MAX_WORKING_EDGE / longestEdge : 1;
  const bitmap =
    ratio === 1
      ? source
      : await createImageBitmap(source, {
          resizeWidth: Math.round(source.width * ratio),
          resizeHeight: Math.round(source.height * ratio),
        });

  const canvas = document.createElement('canvas');
  try {
    for (const region of scanRegions(bitmap.width, bitmap.height)) {
      const found = decodeRegion(bitmap, region, canvas);
      if (found) return found;
    }
    return null;
  } finally {
    bitmap.close();
    if (bitmap !== source) source.close();
  }
}
